import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 10000,
});

export const AUTH_TOKEN_KEY = 'biofleet.authToken';
export const AUTH_USER_KEY = 'biofleet.authUser';
export const getStoredAuthToken = () => (
  sessionStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY)
);
export const clearStoredAuthToken = () => {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
};
export const PROTECTED_DELETE_TIMEOUT_MS = 30000;
const DELETE_RECONCILIATION_TIMEOUT_MS = 15 * 60 * 1000;
export const PROTECTED_DELETE_SUCCEEDED_EVENT = 'biofleet:protected-delete-succeeded';

export const protectedDelete = async (url, config = {}) => {
  const { reconcile, successMessage, ...requestConfig } = config;
  let response;
  try {
    response = await api.delete(url, {
      ...requestConfig,
      timeout: PROTECTED_DELETE_TIMEOUT_MS,
    });
  } catch (error) {
    if (typeof reconcile === 'function' && await reconcile(error)) {
      response = { data: { reconciled: true } };
    } else {
      throw error;
    }
  }
  if (successMessage) {
    window.dispatchEvent(new CustomEvent(PROTECTED_DELETE_SUCCEEDED_EVENT, {
      detail: { message: successMessage },
    }));
  }
  return response;
};

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const waitForResourceDeletion = async (url, error) => {
  if (error?.response?.status === 503) return false;
  const deadline = Date.now() + DELETE_RECONCILIATION_TIMEOUT_MS;
  while (Date.now() < deadline) {
    try {
      await api.get(url);
    } catch (checkError) {
      if (checkError?.response?.status === 404) return true;
    }
    await wait(5_000);
  }
  return false;
};

api.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && !String(error?.config?.url || '').includes('/auth/')) {
      clearStoredAuthToken();
      window.dispatchEvent(new Event('authExpired'));
    }
    return Promise.reject(error);
  },
);

export const authService = {
  login: (payload) => api.post('/auth/login', payload),
  signup: (payload) => api.post('/auth/signup', payload),
  requestPasswordReset: (payload) => api.post('/auth/request-password-reset', payload),
  resetPassword: (payload) => api.post('/auth/reset-password', payload),
  me: () => api.get('/auth/me'),
};

export const userService = {
  getAll: () => api.get('/users'),
  create: (payload) => api.post('/users', payload),
  update: (id, payload) => api.put(`/users/${id}`, payload),
  delete: (id) => protectedDelete(`/users/${id}`),
  getDocuments: (id) => api.get(`/users/${id}/documents`),
  uploadDocument: (id, type, file) => {
    const form = new FormData();
    form.append('document', file);
    return api.post(`/users/${id}/documents/${type}`, form, { timeout: 120000 });
  },
};

export const notificationService = {
  getReminderNotifications: () => api.get('/notifications/reminders'),
};

export const gpsService = {
  getLatestLocations: (params) => api.get('/gps/locations', { params }),
  getDevices: () => api.get('/gps/devices'),
  getLatestTruckLocation: (truckId) => api.get(`/trucks/${truckId}/gps/latest`),
};

export const fileService = {
  uploadInvoice: (file) => {
    const form = new FormData();
    form.append('invoice', file);
    return api.post('/upload/invoice', form, { timeout: 120000 });
  },
  deleteInvoice: (filename) => api.delete(`/upload/invoice/${encodeURIComponent(filename)}`),
};

export const driverLogService = {
  getFuelLogs: ({ all = false } = {}) => api.get('/driver-fuel-logs', {
    params: all ? { scope: 'all' } : undefined,
  }),
  createFuelLog: (payload) => api.post('/driver-fuel-logs', payload),
  deleteFuelLog: (id) => protectedDelete(`/driver-fuel-logs/${id}`, {
    successMessage: 'Fuel record deleted successfully.',
  }),
  getTripLogs: ({ all = false } = {}) => api.get('/driver-trip-logs', {
    params: all ? { scope: 'all' } : undefined,
  }),
  getActiveTrip: () => api.get('/driver-trip-logs/active'),
  beginTrip: (payload) => api.post('/driver-trip-logs/begin', payload),
  saveTripDraft: (id, payload) => api.patch(`/driver-trip-logs/${id}/draft`, payload),
  completeTrip: (id, payload) => api.post(`/driver-trip-logs/${id}/complete`, payload),
  createTripLog: (payload) => api.post('/driver-trip-logs', payload),
  deleteTripLog: (id) => protectedDelete(`/driver-trip-logs/${id}`, {
    successMessage: 'Trip log deleted successfully.',
  }),
};

export const truckService = {
  getAll: (params) => api.get('/trucks', { params }),
  getById: (id) => api.get(`/trucks/${id}`),
  create: (truckData) => api.post('/trucks', truckData),
  update: (id, truckData) => api.put(`/trucks/${id}`, truckData),
  reorder: (orderedIds) => api.put('/trucks/reorder', { orderedIds }),
  archive: (id) => api.put(`/trucks/${id}/archive`),
  unarchive: (id) => api.put(`/trucks/${id}/unarchive`),
  delete: (id) => protectedDelete(`/trucks/${id}`, {
    successMessage: 'Vehicle deleted successfully and preserved in the recovery archive.',
  }),
  getParts: (id) => api.get(`/trucks/${id}/parts`),
  getPhotos: (id) => api.get(`/trucks/${id}/photos`),
  uploadPhotos: (id, files) => {
    const form = new FormData();
    Array.from(files || []).forEach((f) => form.append('photos', f));
    return api.post(`/trucks/${id}/photos`, form, { timeout: 120000 });
  },
  deletePhoto: (truckId, photoId) => protectedDelete(`/trucks/${truckId}/photos/${photoId}`),
  getServiceLogs: (id) => api.get(`/trucks/${id}/service-logs`),
  createServiceLog: (id, payload) => api.post(`/trucks/${id}/service-logs`, payload),
  updateServiceLog: (truckId, logId, payload) => api.put(`/trucks/${truckId}/service-logs/${logId}`, payload),
  deleteServiceLog: (truckId, logId) => protectedDelete(`/trucks/${truckId}/service-logs/${logId}`),
  getServiceLogPhotos: (logId) => api.get(`/service-logs/${logId}/photos`),
  uploadServiceLogPhotos: (logId, formData, onUploadProgress) => api.post(`/service-logs/${logId}/photos`, formData, {
    timeout: 120000,
    onUploadProgress,
  }),
  updateServiceLogPhoto: (logId, photoId, payload) => api.put(`/service-logs/${logId}/photos/${photoId}`, payload),
  deleteServiceLogPhoto: (logId, photoId) => api.delete(`/service-logs/${logId}/photos/${photoId}`),
  getServiceLogPhotoContent: (logId, photoId, params) => api.get(`/service-logs/${logId}/photos/${photoId}/content`, {
    params,
    responseType: 'blob',
  }),
  getServiceAttention: (truckId) => api.get(`/trucks/${truckId}/service-log-attention`),
  getRepairTasks: (id) => api.get(`/trucks/${id}/repairs-needed`),
  createRepairTask: (id, payload) => api.post(`/trucks/${id}/repairs-needed`, payload),
  updateRepairTask: (truckId, taskId, payload) => api.put(`/trucks/${truckId}/repairs-needed/${taskId}`, payload),
  deleteRepairTask: (truckId, taskId) => protectedDelete(`/trucks/${truckId}/repairs-needed/${taskId}`),
  getInspections: (id, params) => api.get(`/trucks/${id}/inspections`, { params }),
  createInspection: (id, payload) => api.post(`/trucks/${id}/inspections`, payload),
  updateInspection: (truckId, inspectionId, payload) => api.put(`/trucks/${truckId}/inspections/${inspectionId}`, payload),
  getInspectionPhotos: (truckId, inspectionId) => api.get(`/trucks/${truckId}/inspections/${inspectionId}/photos`),
  uploadInspectionPhotos: (truckId, inspectionId, files) => {
    const form = new FormData();
    Array.from(files || []).forEach((file) => form.append('photos', file));
    return api.post(`/trucks/${truckId}/inspections/${inspectionId}/photos`, form, { timeout: 120000 });
  },
  deleteInspectionPhoto: (truckId, inspectionId, photoId) => protectedDelete(`/trucks/${truckId}/inspections/${inspectionId}/photos/${photoId}`),
  getGloveBoxDocuments: (id) => api.get(`/trucks/${id}/glove-box`),
  createGloveBoxDocument: (id, payload) => api.post(`/trucks/${id}/glove-box`, payload, { timeout: 120000 }),
  updateGloveBoxDocument: (truckId, documentId, payload) => api.put(`/trucks/${truckId}/glove-box/${documentId}`, payload, { timeout: 120000 }),
  deleteGloveBoxDocument: (truckId, documentId) => protectedDelete(`/trucks/${truckId}/glove-box/${documentId}`),
  getReminders: (id) => api.get(`/trucks/${id}/reminders`),
  createReminder: (id, payload) => api.post(`/trucks/${id}/reminders`, payload, { timeout: 120000 }),
  updateReminder: (truckId, reminderId, payload) => api.put(`/trucks/${truckId}/reminders/${reminderId}`, payload, { timeout: 120000 }),
  deleteReminder: (truckId, reminderId) => protectedDelete(`/trucks/${truckId}/reminders/${reminderId}`),
  deleteReminderFile: (truckId, reminderId, fileId) => protectedDelete(`/trucks/${truckId}/reminders/${reminderId}/files/${fileId}`),
  getWashes: (id) => api.get(`/trucks/${id}/washes`),
  createWash: (id, payload) => api.post(`/trucks/${id}/washes`, payload),
  getConsumables: (id) => api.get(`/trucks/${id}/consumables`),
  replaceConsumable: (truckId, consumableId, payload) => api.post(`/trucks/${truckId}/consumables/${consumableId}/replace`, payload),
  getConsumableMaster: (params) => api.get('/consumables/master', { params }),
  createConsumableMaster: (payload) => api.post('/consumables/master', payload),
  updateConsumableMaster: (id, payload) => api.put(`/consumables/master/${id}`, payload),
  deleteConsumableMaster: (id) => protectedDelete(`/consumables/master/${id}`),
  getAllInspections: (params) => api.get('/inspections', { params }),
  getAllServiceLogs: (params) => api.get('/service-logs', { params }),
};

export const partService = {
  getAll: () => api.get('/parts'),
  getArchived: () => api.get('/parts/archived'),
  getInventory: () => api.get('/parts/inventory'),
  getById: (id) => api.get(`/parts/${id}`),
  create: (partData) => api.post('/parts', partData),
  update: (id, partData) => api.put(`/parts/${id}`, partData),
  assignOne: (id, toTruckId, quantity = 1) => api.post(`/parts/${id}/assign-one`, { to_truck_id: toTruckId, quantity }),
  delete: (id) => protectedDelete(`/parts/${id}`, {
    reconcile: (error) => waitForResourceDeletion(`/parts/${id}`, error),
    successMessage: 'Part archived successfully and can be recovered from the database.',
  }),
  unarchive: (id) => api.put(`/parts/${id}/unarchive`),
  getPhotos: (id) => api.get(`/parts/${id}/photos`),
  uploadPhotos: (id, files) => {
    const form = new FormData();
    Array.from(files || []).forEach((f) => form.append('photos', f));
    return api.post(`/parts/${id}/photos`, form, { timeout: 120000 });
  },
  deletePhoto: (partId, photoId) => protectedDelete(`/parts/${partId}/photos/${photoId}`),
  deletePhotos: (partId, photoIds) => protectedDelete(`/parts/${partId}/photos`, { data: { photoIds } }),
};

export const autosistService = {
  getAll: () => api.get('/autosist'),
  getById: (id) => api.get(`/autosist/${id}`),
  create: (vehicleData) => api.post('/autosist', vehicleData),
  update: (id, vehicleData) => api.put(`/autosist/${id}`, vehicleData),
  delete: (id) => protectedDelete(`/autosist/${id}`),
};

export const fastenerService = {
  getAll: () => api.get('/fasteners'),
  getById: (id) => api.get(`/fasteners/${id}`),
  create: (payload) => api.post('/fasteners', payload),
  update: (id, payload) => api.put(`/fasteners/${id}`, payload),
  delete: (id) => protectedDelete(`/fasteners/${id}`),
};

export default api;
